import { useSidebarStore } from "@/Components/Layout/Store";
import Avatar from "@/Components/Avatar";
import { useLoginStore } from "@/Modules/Auth/Login/Store";
import { showErrorAlert, showSuccessAlert, resolveAvatarUrl } from "@/Utils";
import { useRedirect } from "@/hooks";
import useApi from "@/hooks/useApi";
import { type Ref, useEffect } from "react";
import {
  BellIcon,
  ChevronRightIcon,
  CircleUserIcon,
  LogOutIcon,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import S from "./styles.module.scss";
import { userTypeLabel } from "@/types/IUser";
import { useCompanyStore, useLoggedUserStore } from "@/Store";
import {
  endImpersonationLocally,
  readImpersonationContext,
} from "@/Utils/authSession";

interface BoxProfileProps {
  name?: string;
  email?: string;
  image?: string | null;
  ref?: Ref<HTMLDivElement>;
}

const MENU: {
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
}[] = [
  {
    label: "Minha conta",
    description: "Perfil, senha e dados pessoais",
    to: "/perfil",
    icon: CircleUserIcon,
  },
  {
    label: "Notificações",
    description: "Histórico de avisos",
    to: "/notificacoes",
    icon: BellIcon,
  },
];

const BoxProfile = ({ name, email, image, ref }: BoxProfileProps) => {
  const { closeBoxProfile } = useSidebarStore();
  const redirect = useRedirect();
  const { postRequest, deleteRequest, error } = useApi();
  const { clearSession } = useLoginStore();
  const clearCompanyContext = useCompanyStore(
    (state) => state.clearCompanyContext
  );
  const setLoggedUser = useLoggedUserStore((state) => state.setLoggedUser);
  const userType = useLoggedUserStore((state) => state.loggedUser?.type);
  const impersonation = readImpersonationContext();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeBoxProfile();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeBoxProfile]);

  const handleLogout = async () => {
    if (impersonation) {
      // Só limpa a sessão local após a API confirmar o encerramento; do
      // contrário o acesso assistido continuaria ativo no servidor.
      try {
        await deleteRequest("/api/impersonation/current", undefined, {
          platform: true,
        });
        window.location.assign(endImpersonationLocally());
      } catch (err: unknown) {
        showErrorAlert(
          err instanceof Error
            ? err.message
            : "Não foi possível encerrar o acesso assistido. Tente novamente."
        );
      }
      return;
    }
    try {
      await postRequest("/api/auth/logout", {});
      showSuccessAlert("Sua sessão foi encerrada com sucesso!");
    } catch {
      showErrorAlert(error || "Não foi possível encerrar a sessão na API.");
    } finally {
      clearSession();
      clearCompanyContext();
      setLoggedUser(null);
      closeBoxProfile();
      redirect("/login");
    }
  };

  const typeLabel = userType ? userTypeLabel(userType) : null;

  return (
    <div ref={ref} className={S.panel} aria-label="Menu da conta">
      <div className={S.identity}>
        <Avatar size={44} src={resolveAvatarUrl(image)} />
        <div className={S.identityInfo}>
          <strong title={name}>{name || "—"}</strong>
          <span title={email}>{email}</span>
          {typeLabel && <span className={S.typeChip}>{typeLabel}</span>}
        </div>
      </div>

      <nav className={S.menu}>
        {MENU.map(({ label, description, to, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            onClick={closeBoxProfile}
            className={S.menuItem}
          >
            <span className={S.menuIcon}>
              <Icon size={16} aria-hidden="true" />
            </span>
            <span className={S.menuText}>
              <span>{label}</span>
              <small>{description}</small>
            </span>
            <ChevronRightIcon
              size={16}
              className={S.chevron}
              aria-hidden="true"
            />
          </Link>
        ))}
      </nav>

      <div className={S.footer}>
        <button type="button" className={S.logout} onClick={handleLogout}>
          <LogOutIcon size={16} aria-hidden="true" />
          {impersonation ? "Encerrar acesso assistido" : "Sair da conta"}
        </button>
      </div>
    </div>
  );
};

export default BoxProfile;

